'use client';
import React from 'react';
import { useFormik } from 'formik';
import { RegisterServiceProviderMutationVariables, useRegisterServiceProviderMutation } from 'Generated/graphql';
import FirebaseAuthStage from '../FirebaseAuthStage';
import ServiceProviderAccountDetailsStage from 'Pages/signup/stages/AccountDetailsStage';
import { signupFormValidationSchema } from './fn';
import { AccountType } from 'Common/functions/AccountType';
import ServiceTypeSelectField from '../ServiceTypeSelectField';
import { ServiceType } from '@bookpeazy/common-js';
import { useRouter } from 'next/navigation';
import { AppRoutePath } from 'Pages/routes';
import { useApolloClient } from '@apollo/client';
import FirebaseClient from 'Clients/FireBaseClient';
import signinWithIdToken from 'Common/functions/signinWithIdToken';
import { SearchParam } from 'Common/functions/Search';
import ButtonWithSpinner from '../ButtonWithSpinner';

const serviceProviderSignupServiceTypes = [
  ServiceType.HAIRCUT,
  ServiceType.OIL_CHANGE,
  ServiceType.MASSAGE,
  ServiceType.CAR_WASH,
  ServiceType.DRY_CLEAN,
  ServiceType.PET_GROOMING,
  ServiceType.PHYSIOTHERAPY,
  ServiceType.ESTHETICIAN,
  ServiceType.CHIROPRACTIC,
  ServiceType.WELLNESS,
  ServiceType.DIETITIAN,
  ServiceType.FITNESS_YOGA,
  ServiceType.REFLEXOLOGY,
  ServiceType.REIKI,
  ServiceType.WORKSHOP,
  ServiceType.FLU_CLINIC,
  ServiceType.DENTAL,
];

const getInitialValue = (): Partial<RegisterServiceProviderMutationVariables> => {
  return {
    currency: 'cad',
    country: 'CA',
    timezoneLocation: Intl.DateTimeFormat().resolvedOptions().timeZone,
    businessName: '',
  };
};

const ServiceProviderSignupForm: React.FC = () => {
  const [registerServiceProvider] = useRegisterServiceProviderMutation();
  const router = useRouter();
  const client = useApolloClient();

  const formik = useFormik({
    initialValues: getInitialValue(),
    enableReinitialize: false,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: signupFormValidationSchema,
    onSubmit: async (values) => {
      if (values.idToken) {
        await registerServiceProvider({
          variables: values as RegisterServiceProviderMutationVariables,
        });

        const updatedToken =
          await FirebaseClient.getCurrentUser()?.getIdToken(true);

        if (updatedToken) {
          await signinWithIdToken(
            updatedToken,
            true,
            d => {
              client.resetStore().finally(() => {
                const redirect = AppRoutePath.SERVICE_PROVIDER_DASHBOARD_ONBOARDING.replace('[stageSlug]', 'services');
                if (d.fullName) {
                  router.push(redirect);
                } else {
                  const url = `${AppRoutePath.UPDATE_USERNAME}?${SearchParam.RETURN_TO}=${redirect}`;
                  router.push(url);
                }

              });
            }
          );
        }
      }
    },
  });

  const [step, setStep] = React.useState(0);
  const [tosChecked, setTosChecked] = React.useState(false);

  const handleSignedIn = () => {
    setStep(1);
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <FirebaseAuthStage
            tosChecked={tosChecked}
            setTosChecked={setTosChecked}
            onSignedIn={handleSignedIn}
            setFieldValue={formik.setFieldValue}
            accountType={AccountType.ServiceProvider}
          />
        );
      case 1:
        return (
          <label className="flex flex-col gap-2">
            Service Category
            <ServiceTypeSelectField
              onSelect={() => setStep(2)}
              types={serviceProviderSignupServiceTypes}
              value={formik.values.serviceType as ServiceType || null}
              onChange={(value) => formik.setFieldValue('serviceType', value)}
              error={formik.errors.serviceType}
              touched={formik.touched.serviceType}
              help="Please select a service type"
            />
          </label>
        );
      case 2:
        return (
          <ServiceProviderAccountDetailsStage formik={formik} />
        );
      default:
        return null;
    }
  };

  return (
    <form className="w-full md:w-3/4 px-6" onSubmit={formik.handleSubmit}>
      {renderStep()}
      {step === 2 && (
        <ButtonWithSpinner
          isLoading={formik.isSubmitting}
        />
      )}
    </form>
  );
};

export default ServiceProviderSignupForm;
